/**
 * Автоматическая копия за день.
 *
 * Резервная копия, которую нужно вспомнить и нажать, — это копия, которой нет.
 * Человек исправно жмёт «Скачать» первые две недели, потом перестаёт, а теряет
 * телефон на третий год. Поэтому копия снимается сама.
 *
 * ## Почему при запуске, а не по расписанию
 *
 * Фоновой выгрузки «пока человек спит» в вебе не существует: единственный
 * механизм — Periodic Background Sync — есть только в Chromium, только для
 * установленного приложения, и браузер сам решает, дать ли его. Обещать на этом
 * «у вас всегда есть вчерашняя копия» нельзя.
 *
 * Зато планировщик открывают почти каждый день — и копия, снятая при первом за
 * сутки открытии, оказывается почти всегда свежей. Это работает во всех
 * браузерах и не требует ни разрешений, ни диалогов.
 *
 * ## Куда ложится копия
 *
 * В приложении на телефоне — настоящим файлом в документы, молча. В браузере
 * записать файл на диск без спроса нельзя, поэтому копия остаётся снимком
 * внутри приложения. Это честная защита от беды внутри приложения — ошибочной
 * замены всех данных, массового удаления, сбоя — но не от потери устройства:
 * снимок исчезнет вместе с данными сайта. Поэтому карточка копии продолжает
 * просить файл, и чем дольше его нет, тем настойчивее.
 */

import { db } from '@/shared/db/database';
import type { BackupSnapshot } from '@/shared/db/schema';
import { createId } from '@/shared/lib/id';
import { addDays, type IsoDate, today as todayIso } from '@/shared/lib/datetime';

import { pruneBackupFiles } from './backup-files';
import { serializeBackup } from './backup-payload';
import { folderPermission, readBackupFolder, writeBackupToFolder } from './cloud-folder';
import { uploadBackupForDay } from './disk-backup';
import { buildBackup, countBackupRecords, type Backup } from './export-data';
import { INTEGRITY_MESSAGE, verifyBackup } from './integrity';
import { planRetention } from './retention';
import { isNativeApp, writeBackupFile } from './save-backup';
import { packSnapshot, unpackSnapshot } from './snapshot-pack';

/** Через сколько дней копия считается устаревшей. */
export const BACKUP_STALE_DAYS = 7;

/**
 * Ключ последней копии — любой, хоть автоматической, хоть скачанной руками.
 *
 * На владельца, а не на устройство: в одной базе может быть заведено несколько
 * человек, и общая отметка означала бы, что второй остаётся без копии — за
 * сегодня она «уже есть», только чужая.
 */
const lastBackupKey = (userId: string) => `backup.last:${userId}`;

/** Куда легла последняя копия — от этого зависит, что сказать человеку. */
export type BackupPlace = 'snapshot' | 'file' | 'download' | 'folder';

export interface LastBackup {
  /** День, когда копия снята. */
  date: IsoDate;
  at: number;
  records: number;
  place: BackupPlace;
  /** Путь к файлу на устройстве, если копия легла файлом. */
  path: string | null;
}

/** Запоминает факт копии. Ручное скачивание — такая же копия, как автоматическая. */
export async function rememberBackup(userId: string, last: LastBackup): Promise<void> {
  await db.settings.put({ key: lastBackupKey(userId), value: last, updatedAt: Date.now() });
}

export async function readLastBackup(userId: string): Promise<LastBackup | null> {
  const row = await db.settings.get(lastBackupKey(userId));
  const value = row?.value as Partial<LastBackup> | undefined;

  if (!value || typeof value.date !== 'string' || typeof value.records !== 'number') return null;

  return {
    date: value.date,
    at: typeof value.at === 'number' ? value.at : 0,
    records: value.records,
    place: value.place ?? 'snapshot',
    path: value.path ?? null,
  };
}

/** Копия старее недели — или её нет вовсе. */
export function isBackupStale(last: LastBackup | null, today: IsoDate = todayIso()): boolean {
  return last === null || last.date < addDays(today, -BACKUP_STALE_DAYS);
}

/** Копии, лежащие внутри приложения, — от свежей к старой. */
export async function listSnapshots(userId: string): Promise<BackupSnapshot[]> {
  const rows = await db.backups.where('userId').equals(userId).toArray();
  return rows.filter((row) => row.payload !== null).sort((a, b) => b.date.localeCompare(a.date));
}

/**
 * Разбирает выбранный снимок.
 *
 * `id` не задан — берётся самый свежий: чаще всего восстанавливаются именно
 * «на вчера».
 */
export async function readSnapshot(userId: string, id?: string): Promise<Backup | null> {
  const rows = await listSnapshots(userId);
  const chosen = id ? rows.find((row) => row.id === id) : rows[0];

  if (!chosen?.payload) return null;

  try {
    const backup = JSON.parse(await unpackSnapshot(chosen.payload)) as Backup;

    // Снимок проверяется и при записи, но между записью и восстановлением
    // проходят месяцы, а хранилище браузера не даёт гарантий на такой срок.
    // Восстановление из порченой копии хуже отказа: оно стирает то, что ещё
    // оставалось.
    return verifyBackup(backup) === null ? backup : null;
  } catch {
    // Испорченный снимок молча выдавать за копию нельзя: человек нажмёт
    // «восстановить» и получит пустоту вместо данных.
    return null;
  }
}

/**
 * Автоматические копии включены.
 *
 * Отключаемо — и это не уступка, а необходимость: копия содержит записи о
 * здоровье и лекарствах, а файл лежит в общих документах телефона, где его
 * видно из файлового менеджера. Человек вправе решить, что не хочет этого, и
 * снимать копию только руками.
 */
const autoBackupKey = (userId: string) => `backup.auto:${userId}`;

export async function isAutoBackupEnabled(userId: string): Promise<boolean> {
  const row = await db.settings.get(autoBackupKey(userId));
  // По умолчанию включено: копия, которую нужно вспомнить и нажать, — это
  // копия, которой нет.
  return row?.value !== false;
}

export async function setAutoBackupEnabled(userId: string, enabled: boolean): Promise<void> {
  await db.settings.put({ key: autoBackupKey(userId), value: enabled, updatedAt: Date.now() });
}

/**
 * Снимает копию, если сегодня её ещё не снимали.
 *
 * Возвращает `null`, когда делать нечего: копия за этот день уже есть или
 * автоматические копии выключены. Проверка идёт по дню, а не по времени, —
 * человек открывает приложение десятки раз за сутки, и снимать копию на каждое
 * открытие бессмысленно.
 */
export async function runDailyBackup(
  userId: string,
  today: IsoDate = todayIso(),
): Promise<LastBackup | null> {
  if (!(await isAutoBackupEnabled(userId))) return null;

  const last = await readLastBackup(userId);
  if (last !== null && last.date >= today) return null;

  const backup = await buildBackup(userId);
  const records = countBackupRecords(backup);
  const at = Date.now();

  /*
    Текст файла готовится один раз на все пути наружу — телефон, папка облака,
    Яндекс.Диск. Если он закрыт паролем, закрыт везде: незашифрованной копии,
    уехавшей одним из путей, человек бы не заметил.
  */
  const content = await serializeBackup(userId, backup);

  /*
    Снимок внутри приложения делается всегда: в нём двадцать с лишним точек
    возврата, а в папке лежит последнее состояние и начало месяца. Это разные
    защиты — от беды внутри приложения и от потери устройства, — и одна другую
    не заменяет.

    Сбой записи не должен ронять запуск приложения, но и выдавать себя за
    удачную копию не должен: день остаётся неотмеченным, следующее открытие
    попробует снова, а карточка тем временем честно скажет, что копии нет.
  */
  if (!isNativeApp()) {
    try {
      await storeSnapshot(userId, today, backup, records);
    } catch {
      return null;
    }
  }

  /*
    Выгрузка на Диск — единственный здесь путь, уводящий копию за пределы
    устройства без участия человека. Она идёт после записи на телефон и её не
    отменяет: файл есть даже без сети, а неудача выгрузки запоминается и
    показывается в карточке.
  */
  await uploadBackupForDay(userId, backup, { today, content }).catch(() => null);

  const folder = await writeToFolder(backup, content);
  if (folder) return finish(userId, { date: today, at, records, place: 'folder', path: folder });

  if (isNativeApp()) {
    const path = await writeBackupFile(backup, content);

    // Файлы прорежаются сразу после записи: иначе за три года в документах
    // накопилась бы тысяча копий, и найти среди них нужную стало бы отдельной
    // задачей.
    await pruneBackupFiles();

    return finish(userId, { date: today, at, records, place: 'file', path });
  }

  return finish(userId, { date: today, at, records, place: 'snapshot', path: null });
}

async function finish(userId: string, result: LastBackup): Promise<LastBackup> {
  await rememberBackup(userId, result);
  return result;
}

/**
 * Пишет копию в папку человека, если она указана и доступна.
 *
 * Возвращает имя папки — или `null`, когда писать некуда: папку не выбирали,
 * доступ к ней ещё не подтвердили после перезапуска браузера или её больше нет
 * (флешку вынули, каталог переименовали). Молчаливый отказ здесь правильный:
 * копия всё равно ложится внутрь приложения, а карточка скажет, что доступ
 * нужно подтвердить.
 */
async function writeToFolder(backup: Backup, content: string): Promise<string | null> {
  const folder = await readBackupFolder();
  if (!folder) return null;

  try {
    if ((await folderPermission(folder)) !== 'granted') return null;
    await writeBackupToFolder(folder, backup, content);
    return folder.name;
  } catch {
    return null;
  }
}

/**
 * Кладёт копию в папку прямо сейчас — сразу после того, как её выбрали.
 *
 * Человек указал папку и ждёт, что файл там появится; предлагать ему подождать
 * до завтрашнего открытия было бы издевательством. Заодно это единственная
 * проверка, что папка и правда доступна на запись.
 */
export async function backupToFolderNow(userId: string): Promise<LastBackup | null> {
  const folder = await readBackupFolder();
  if (!folder) return null;

  const backup = await buildBackup(userId);
  await writeBackupToFolder(folder, backup, await serializeBackup(userId, backup));

  const result: LastBackup = {
    date: todayIso(),
    at: Date.now(),
    records: countBackupRecords(backup),
    place: 'folder',
    path: folder.name,
  };

  await rememberBackup(userId, result);
  return result;
}

/**
 * Кладёт снимок в базу и прореживает старые.
 *
 * Копии редеют с возрастом — неделя по дням, год по месяцам, три года по
 * годам, — и правило этого прореживания живёт отдельно (`retention.ts`), где
 * его можно проверить, не трогая базу.
 *
 * Сжатие и прореживание — одна пара: без предела копии заняли бы больше места,
 * чем сами данные, а без сжатия двадцать две выгрузки вытеснили бы из
 * хранилища то, что берегут.
 */
async function storeSnapshot(
  userId: string,
  date: IsoDate,
  backup: Backup,
  records: number,
): Promise<void> {
  const snapshot: BackupSnapshot = {
    id: createId(),
    userId,
    date,
    createdAt: Date.now(),
    records,
    path: null,
    payload: await packSnapshot(JSON.stringify(backup)),
  };

  await db.transaction('rw', db.backups, async () => {
    const previous = await db.backups.where('userId').equals(userId).toArray();

    // Снимок за этот день мог остаться от прежнего запуска — он же и
    // заменяется: две копии за одну дату не нужны никому.
    const sameDay = previous.filter((row) => row.date === date).map((row) => row.id);
    await db.backups.bulkDelete(sameDay);

    const { drop } = planRetention([
      snapshot,
      ...previous.filter((row) => !sameDay.includes(row.id)),
    ]);

    await db.backups.bulkDelete(drop.map((row) => row.id));
    await db.backups.add(snapshot);
  });

  /*
    Снимок перечитывается сразу после записи — целиком, тем же путём, каким его
    потом будет читать восстановление. Копия, которая не разжалась или потеряла
    половину записей, ничем не выдаёт себя со стороны: в списке она выглядит
    ровно как настоящая, и человек узнаёт правду в худший момент. Битый снимок
    лучше удалить сразу: пусть в списке будет на одну копию меньше, чем на одну
    ложную.
  */
  const written = await readSnapshot(userId, snapshot.id);

  /*
    Сверяется и с исходником, и сам с собой. Одной внутренней проверки мало:
    распакуйся снимок во что-то другое целиком — в пустой объект, в обрывок
    чужого файла, — он был бы «целым» по собственной сумме, которой у него нет.
  */
  const problem =
    written === null || written.checksum !== backup.checksum ? 'checksum' : verifyBackup(written);

  if (problem) {
    await db.backups.delete(snapshot.id);
    throw new Error(INTEGRITY_MESSAGE[problem]);
  }
}
